/* Common Imports */

import {
	CheckCircleRounded,
	Close,
	ContentCopyRounded,
	EditOutlined,
	InfoOutlined,
	VerifiedRounded,
} from "@mui/icons-material";
import { Alert, ButtonBase, CircularProgress, IconButton, Paper, Snackbar, TextField, Tooltip, Typography } from "@mui/material";
import { styled, useMediaQuery, useTheme } from "@mui/system";
import { ObjectId } from "mongodb";
import { NextRouter } from "next/router";
import React from "react";

/* Component Imports */

import BeegruButton from "../../../../common-components/buttons/BeegruButton";

/* Type Imports */

import { BusinessProfileInterface, UserInterface } from "../../../../../models";
import StateInterface from "../../../../../redux-magic/state-interface";
import { setPropertySnackbarThunk, UpdatePropertyRegulatoryInfoThunk } from "../../../../../redux-magic/thunks";
import { confirmSendForReview, skipNextSendForReviewConfirm } from "../../SendForReviewConfirm";
import { confirmDiscardChanges } from "../../SectionDiscardConfirm";

/* Design tokens (per spec for mobile redesign) */

const COLOR_HEADING = "#1A1A1A";
const COLOR_LABEL = "#666666";
const COLOR_VALUE = "#000000";
const COLOR_LINK = "#2E5BFF";
const COLOR_CHIP_BORDER = "#D1D9FF";
const COLOR_FIELD_CARD_BORDER = "rgba(0, 0, 0, 0.12)";

/* Styled Components — desktop mirrors Figma 1859:4264/4412; mobile mirrors the redesigned spec. */

const Container = styled(Paper)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	alignItems: "flex-start",
	width: "100%",
	borderRadius: "16px",
	boxShadow: "none",
	background: theme.palette.background.paper,
	gap: "0.5rem",
	padding: "1rem",
	[theme.breakpoints.up("md1190")]: {
		padding: "1.5rem",
	},
}));

const HeaderWrap = styled("div")<{ compact?: boolean }>(({ compact, theme }) => ({
	display: "flex",
	flexDirection: "column",
	width: "100%",
	paddingBottom: "1rem",
	[theme.breakpoints.up("md1190")]: {
		paddingBottom: compact ? "0.5rem" : "1rem",
	},
}));

const HeaderRow = styled("div")(() => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
	width: "100%",
	gap: "0.5rem",
}));

const HeadingGroup = styled("div")(() => ({
	display: "inline-flex",
	alignItems: "center",
	gap: "0.5rem",
	flexShrink: 1,
	minWidth: 0,
}));

const Heading = styled(Typography)(({ theme }) => ({
	width: "100%",
	fontWeight: "500",
	fontSize: "1rem",
	[theme.breakpoints.up("sm")]: {
		fontSize: "1rem",
	},
	[theme.breakpoints.up("md1190")]: {
		fontSize: "1.125rem",
	},
}));

/* Desktop 2-col grid */

const TwoColumnGrid = styled("div")(({ theme }) => ({
	display: "grid",
	gridTemplateColumns: "1fr 1fr",
	width: "100%",
	rowGap: "2.25rem",
	columnGap: "1.5rem",
}));

const ReadCell = styled("div")(() => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "flex-start",
	width: "100%",
	gap: "0.5rem",
}));

const EditCell = styled("div")(() => ({
	display: "flex",
	flexDirection: "column",
	gap: "0.375rem",
	width: "100%",
}));

const LabelWrap = styled("div")(() => ({
	display: "inline-flex",
	alignItems: "center",
	gap: "0.25rem",
	flexShrink: 0,
}));

const FieldLabelDesktop = styled(Typography)(() => ({
	fontWeight: 400,
	fontSize: "1rem",
	lineHeight: "1.5rem",
	color: COLOR_HEADING,
	whiteSpace: "nowrap",
}));

const FieldValueDesktop = styled(Typography)<{ multi?: boolean }>(({ multi }) => ({
	fontWeight: 400,
	fontSize: "1rem",
	lineHeight: multi ? "2.25rem" : "1.5rem",
	color: COLOR_HEADING,
	textAlign: "right",
	overflowWrap: "anywhere",
	flex: "1 1 auto",
	minWidth: 0,
	whiteSpace: multi ? "pre-line" : "normal",
}));

/* Field cards (used at all widths; layout swaps via responsive grid) */

const FieldCardGrid = styled("div")(({ theme }) => ({
	display: "grid",
	gridTemplateColumns: "1fr",
	width: "100%",
	rowGap: "0.75rem",
	columnGap: "1rem",
	[theme.breakpoints.up("md1190")]: {
		gridTemplateColumns: "1fr 1fr",
		rowGap: "1rem",
		columnGap: "1.25rem",
	},
}));

const FieldCard = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	width: "100%",
	border: `1px solid ${COLOR_FIELD_CARD_BORDER}`,
	borderRadius: "0.75rem",
	padding: "0.875rem 1rem",
	gap: "0.5rem",
	[theme.breakpoints.up("md1190")]: {
		padding: "1rem 1.25rem",
	},
}));

const PermitTopRow = styled("div")(() => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "flex-start",
	width: "100%",
	gap: "0.5rem",
}));

const PermitLabelGroup = styled("div")(() => ({
	display: "flex",
	flexDirection: "column",
	alignItems: "flex-start",
	gap: "0.125rem",
	minWidth: 0,
}));

const FieldLabelMobile = styled(Typography)(({ theme }) => ({
	fontWeight: 500,
	fontSize: "0.875rem",
	lineHeight: "1.25rem",
	color: COLOR_LABEL,
	whiteSpace: "nowrap",
	[theme.breakpoints.up("md1190")]: {
		fontSize: "1rem",
		lineHeight: "1.5rem",
	},
}));

const FieldLabelSub = styled(Typography)(({ theme }) => ({
	fontWeight: 500,
	fontSize: "0.75rem",
	lineHeight: "1.125rem",
	color: COLOR_LABEL,
	whiteSpace: "nowrap",
	[theme.breakpoints.up("md1190")]: {
		fontSize: "0.875rem",
		lineHeight: "1.25rem",
	},
}));

const FieldValueMobile = styled(Typography)(({ theme }) => ({
	fontWeight: 600,
	fontSize: "0.875rem",
	lineHeight: "1.25rem",
	color: COLOR_VALUE,
	textAlign: "right",
	whiteSpace: "nowrap",
	overflow: "hidden",
	textOverflow: "ellipsis",
	flex: "1 1 auto",
	minWidth: 0,
	[theme.breakpoints.up("md1190")]: {
		fontSize: "1rem",
	},
}));

const SimpleFieldRow = styled("div")(() => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
	width: "100%",
	gap: "0.5rem",
}));

const ActionLink = styled(ButtonBase)(({ theme }) => ({
	cursor: "pointer",
	fontFamily: "inherit",
	fontWeight: 600,
	fontSize: "0.75rem",
	lineHeight: "1.125rem",
	color: COLOR_LINK,
	whiteSpace: "nowrap",
	textDecoration: "none",
	padding: "0.25rem 0.5rem",
	borderRadius: "0.375rem",
	[theme.breakpoints.up("md1190")]: {
		fontSize: "0.875rem",
		lineHeight: "1.25rem",
	},
}));

const CopyIconButton = styled(ButtonBase)(() => ({
	cursor: "pointer",
	display: "inline-flex",
	alignItems: "center",
	justifyContent: "center",
	color: COLOR_VALUE,
	padding: "0.375rem",
	borderRadius: "50%",
}));

const PermitValueRow = styled("div")(() => ({
	display: "flex",
	alignItems: "center",
	gap: "0.5rem",
	flex: "1 1 auto",
	minWidth: 0,
	maxWidth: "100%",
	justifyContent: "flex-end",
}));

const PermitScrollableValue = styled("div")(({ theme }) => ({
	flex: "0 1 auto",
	minWidth: 0,
	overflowX: "auto",
	whiteSpace: "nowrap",
	fontWeight: 600,
	fontSize: "0.875rem",
	lineHeight: "1.25rem",
	color: COLOR_VALUE,
	textAlign: "right",
	scrollbarWidth: "none",
	msOverflowStyle: "none",
	"&::-webkit-scrollbar": {
		display: "none",
	},
	[theme.breakpoints.up("md1190")]: {
		fontSize: "1rem",
		lineHeight: "1.5rem",
	},
}));

const ChipRow = styled("div")(() => ({
	display: "flex",
	flexDirection: "row",
	flexWrap: "wrap",
	alignItems: "center",
	gap: "0.5rem",
	width: "100%",
}));

const CplChip = styled("div")(() => ({
	display: "inline-flex",
	alignItems: "center",
	gap: "0.25rem",
	padding: "0.375rem 0.75rem",
	border: `1px solid ${COLOR_CHIP_BORDER}`,
	borderRadius: "999px",
	background: "#FFFFFF",
	fontFamily: "inherit",
	fontWeight: 500,
	fontSize: "0.75rem",
	lineHeight: "1.125rem",
	color: COLOR_LINK,
	maxWidth: "10rem",
}));

const ChipText = styled("span")(() => ({
	overflow: "hidden",
	textOverflow: "ellipsis",
	whiteSpace: "nowrap",
}));

const MoreChip = styled("button")(() => ({
	all: "unset",
	cursor: "pointer",
	display: "inline-flex",
	alignItems: "center",
	gap: "0.25rem",
	padding: "0.375rem 0.75rem",
	border: `1px solid ${COLOR_FIELD_CARD_BORDER}`,
	borderRadius: "999px",
	background: "#FFFFFF",
	fontWeight: 600,
	fontSize: "0.75rem",
	lineHeight: "1.125rem",
	color: COLOR_LINK,
	"&:focus-visible": {
		outline: `2px solid ${COLOR_LINK}`,
		outlineOffset: "2px",
	},
}));

const ButtonRow = styled("div")(() => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "stretch",
	width: "100%",
	padding: "1rem 0rem 0rem 0rem",
}));

//? Tooltip copy is provisional — final wording to be supplied by product.
const tooltipCopy = {
	madhmoun: "Madhmoun is the Abu Dhabi real-estate property regulatory permit identifier issued by the DMT.",
	bln: "BLN is the broker license number issued to the property's listing agent.",
	adm: "ADM is the agent license number issued by the Abu Dhabi Department of Economic Development.",
	cpl: "CPL is the Company Professional License issued by ADDED/ADREC authorising sales, rental and brokerage activity.",
};

const ClickableInfoTooltip = ({
	title,
	isOpen,
	onToggle,
	mobile = false,
}: {
	title: string;
	isOpen: boolean;
	onToggle: () => void;
	mobile?: boolean;
}) => {
	const theme = useTheme();
	const trigger = (
		<span
			style={{
				display: "inline-flex",
				alignItems: "center",
				lineHeight: 0,
			}}
		>
			<ButtonBase
				centerRipple
				onClick={(e) => {
					e.stopPropagation();
					if (mobile) onToggle();
				}}
				sx={{
					padding: "0.125rem",
					borderRadius: "50%",
					color: theme.palette.text.secondary,
					flexShrink: 0,
					lineHeight: 0,
				}}
				aria-label="More info"
			>
				<InfoOutlined
					sx={{
						fontSize: "1rem",
						[theme.breakpoints.up("md1190")]: { fontSize: "1.25rem" },
					}}
				/>
			</ButtonBase>
		</span>
	);

	//? Mobile: click-controlled tooltip (taps don't have hover). Desktop: native hover/focus tooltip.
	if (mobile) {
		return (
			<Tooltip
				title={title}
				open={isOpen}
				arrow
				placement="top"
				disableHoverListener
				disableFocusListener
				disableTouchListener
			>
				{trigger}
			</Tooltip>
		);
	}
	return (
		<Tooltip
			title={title}
			arrow
			placement="top"
			enterDelay={100}
			leaveDelay={100}
		>
			{trigger}
		</Tooltip>
	);
};

const truncateMiddle = (value: string, max: number = 12): string => {
	if (!value) return "";
	if (value.length <= max) return value;
	return `${value.slice(0, max - 3)}...`;
};

const RegulatoryInfoCard = ({
	router,
	dispatch,
	session,
	profile_context,
	property_id,
	property_rera_number,
	location_country,
	property_regulatory_permit,
	property_broker_license,
	property_created_by,
	is_user_creator,
	logged_user_access,
	logged_user,
	assigned_to,
	isEditor,
	regulatory_info_snackbar,
}: {
	router: NextRouter;
	dispatch: Function;
	session: any;
	profile_context: StateInterface["profile_context"];
	property_id: string | ObjectId;
	property_rera_number?: string;
	location_country?: string;
	property_regulatory_permit?: { permit_number: string; issuing_authority: string };
	property_broker_license?: { permit_number: string; permit_name: string };
	property_created_by: {
		is_business_profile: boolean;
		business_profile_id: string | ObjectId | BusinessProfileInterface;
		user_id: string | ObjectId | UserInterface;
	};
	is_user_creator: boolean;
	logged_user_access: string;
	logged_user: string | ObjectId;
	assigned_to: Array<string | ObjectId>;
	isEditor: boolean;
	regulatory_info_snackbar: boolean;
}) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("md1190"));

	const localLang = {
		title:
			router.locale === "ar-AE"
				? "المعلومات التنظيمية"
				: router.locale === "kn-IN"
					? "ನಿಯಂತ್ರಣ ಮಾಹಿತಿ"
					: "Regulatory Information",
		permit: router.locale === "ar-AE" ? "تصريح" : router.locale === "kn-IN" ? "ಪರ್ಮಿಟ್" : "Permit",
		madhmoun_sub: router.locale === "ar-AE" ? "(مضمون)" : router.locale === "kn-IN" ? "(ಮಧ್ಮೂನ್)" : "(Madhmoun)",
		permit_madhmoun_inline:
			router.locale === "ar-AE"
				? "تصريح (مضمون)"
				: router.locale === "kn-IN"
					? "ಪರ್ಮಿಟ್ (ಮಧ್ಮೂನ್)"
					: "Permit (Madhmoun)",
		bln: "BLN",
		adm: "ADM",
		cpl: "CPL",
		madhmoun_placeholder:
			router.locale === "ar-AE"
				? "أدخل رقم التصريح"
				: router.locale === "kn-IN"
					? "ಪರವಾನಗಿ ಸಂಖ್ಯೆಯನ್ನು ನಮೂದಿಸಿ"
					: "Enter permit number",
		bln_placeholder:
			router.locale === "ar-AE"
				? "أدخل رقم الرخصة"
				: router.locale === "kn-IN"
					? "BLN ಸಂಖ್ಯೆಯನ್ನು ನಮೂದಿಸಿ"
					: "Enter BLN number",
		update: router.locale === "ar-AE" ? "تحديث" : router.locale === "kn-IN" ? "ನವೀಕರಿಸಿ" : "Update",
		verify_permit:
			router.locale === "ar-AE" ? "تحقق من التصريح" : router.locale === "kn-IN" ? "ಪರ್ಮಿಟ್ ಪರಿಶೀಲಿಸಿ" : "Verify Permit",
		more: router.locale === "ar-AE" ? "المزيد" : router.locale === "kn-IN" ? "ಇನ್ನಷ್ಟು" : "More",
		copied: router.locale === "ar-AE" ? "تم النسخ" : router.locale === "kn-IN" ? "ನಕಲಿಸಲಾಗಿದೆ" : "Copied",
		rera: router.locale === "ar-AE" ? "رقم ريرا" : router.locale === "kn-IN" ? "ರೇರಾ ಸಂಖ್ಯೆ" : "RERA number",
		rera_placeholder:
			router.locale === "ar-AE"
				? "أدخل رقم ريرا"
				: router.locale === "kn-IN"
					? "ರೇರಾ ಸಂಖ್ಯೆಯನ್ನು ನಮೂದಿಸಿ"
					: "Enter RERA number",
		snackValue:
			router.locale === "ar-AE"
				? "تم تحديث المعلومات التنظيمية بنجاح"
				: router.locale === "kn-IN"
					? "ನಿಯಂತ್ರಣ ಮಾಹಿತಿ ಯಶಸ್ವಿಯಾಗಿ ನವೀಕರಿಸಲಾಗಿದೆ"
					: "Regulatory information updated successfully",
	};

	//? India listings edit only a RERA number; UAE (Abu Dhabi) edit the permit/broker fields.
	const isIndia = (location_country || "").toLowerCase() === "india";
	const reraNumber = property_rera_number || "";

	//? madhmoun + bln come from the property document
	const madhmounNumber =
		(property_regulatory_permit?.issuing_authority || "").toLowerCase() === "madhmoun"
			? property_regulatory_permit?.permit_number || ""
			: "";

	const blnNumber =
		(property_broker_license?.permit_name || "").toLowerCase() === "bln"
			? property_broker_license?.permit_number || ""
			: "";

	//? adm + cpl come from the creator's business profile (read-only here; edit on BP page)
	const creatorBP =
		property_created_by?.is_business_profile && typeof property_created_by?.business_profile_id === "object"
			? (property_created_by.business_profile_id as BusinessProfileInterface)
			: null;

	const permits = creatorBP?.regulatory_permits || [];
	const adm = permits.find((p) => (p.permit_name || "").toLowerCase() === "adm");
	const cpls = permits.filter((p) => (p.permit_name || "").toLowerCase() === "cpl" && p.permit_number);

	const admNumber = adm?.permit_number || "";
	const cplNumbers = cpls.map((p) => p.permit_number).filter(Boolean);
	const cplDisplayDesktop =
		cplNumbers.length > 0 ? cplNumbers.map((n, i) => (i < cplNumbers.length - 1 ? `${n},` : n)).join("\n") : "—";

	//? Edit gate matches AboutDeveloperCard convention
	const access = logged_user_access || "";
	const assigned: Array<string | ObjectId> = assigned_to || [];
	const canEdit =
		is_user_creator ||
		access.includes("super_super_admin") ||
		access.includes("super_admin") ||
		(access.includes("admin") &&
			access.includes("property") &&
			assigned.some((a) => a?.toString() === (logged_user as string)?.toString())) ||
		isEditor;

	const [isEditing, setIsEditing] = React.useState<boolean>(false);
	const [madhmounDraft, setMadhmounDraft] = React.useState<string>(madhmounNumber);
	const [blnDraft, setBlnDraft] = React.useState<string>(blnNumber);
	const [reraDraft, setReraDraft] = React.useState<string>(reraNumber);
	const [isSaving, setIsSaving] = React.useState<boolean>(false);
	const [showAllCpl, setShowAllCpl] = React.useState<boolean>(false);
	const [copyTarget, setCopyTarget] = React.useState<string>("");
	const [openTip, setOpenTip] = React.useState<string>("");
	const tipTimerRef = React.useRef<number | null>(null);

	const handleTipToggle = (key: string) => {
		if (tipTimerRef.current) {
			window.clearTimeout(tipTimerRef.current);
			tipTimerRef.current = null;
		}
		setOpenTip((prev) => {
			if (prev === key) return "";
			tipTimerRef.current = window.setTimeout(() => {
				setOpenTip("");
				tipTimerRef.current = null;
			}, 4000);
			return key;
		});
	};

	React.useEffect(() => {
		return () => {
			if (tipTimerRef.current) {
				window.clearTimeout(tipTimerRef.current);
			}
		};
	}, []);

	//? Sync drafts when redux value updates (e.g. after save)
	React.useEffect(() => {
		if (!isEditing) {
			setMadhmounDraft(madhmounNumber);
			setBlnDraft(blnNumber);
			setReraDraft(reraNumber);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [madhmounNumber, blnNumber, reraNumber]);

	//? Close edit mode once the save snackbar fires
	React.useEffect(() => {
		if (regulatory_info_snackbar && isEditing) {
			setIsEditing(false);
			setIsSaving(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [regulatory_info_snackbar]);

	const phone = session?.user?.dbUser?.phone || "";
	const countryCode = session?.user?.dbUser?.country_code || "";

	const handleSave = async () => {
		/* Saving unlists the listing until it is approved again: say so first (gap A3, the app's prompt). */
		if (!(await confirmSendForReview())) return;
		setIsSaving(true);
		try {
			await dispatch(
				UpdatePropertyRegulatoryInfoThunk({
					sessionId: session?.session_id || session?.user?.dbUser?.session_id || "",
					property_id: property_id,
					...(isIndia
						? { rera_number: reraDraft.trim() }
						: {
								regulatory_permit_number: madhmounDraft.trim(),
								broker_license_number: blnDraft.trim(),
							}),
					editor_is_business_profile: isEditor
						? (profile_context?.is_business_profile as boolean)
						: (property_created_by?.is_business_profile as boolean),
					editor_user_id: isEditor
						? profile_context?.user_id
						: typeof property_created_by?.user_id === "object"
							? (property_created_by.user_id as UserInterface)._id
							: (property_created_by?.user_id as string),
					editor_business_profile_id: isEditor
						? (profile_context?.business_profile_id as string | ObjectId)
						: typeof property_created_by?.business_profile_id === "object"
							? (property_created_by.business_profile_id as BusinessProfileInterface)._id
							: (property_created_by?.business_profile_id as string),
					url: router.asPath,
					phoneNumber: phone,
					countryCode: countryCode,
					profile_context: profile_context,
				}),
			);
			//? Close edit mode directly off the dispatch resolution — the snackbar useEffect alone
			//? misses subsequent saves because the flag is sticky-true after the first success.
			setIsEditing(false);
		} finally {
			setIsSaving(false);
		}
	};

	const handleCancel = async () => {
		/* Changed numbers ask first (3 Nov 2026, the app's "Discard changes?"). */
		if (madhmounDraft !== madhmounNumber || blnDraft !== blnNumber || reraDraft !== reraNumber) {
			const answer = await confirmDiscardChanges();
			if (answer === "keep") return;
			if (answer === "save") {
				skipNextSendForReviewConfirm();
				await handleSave();
				return;
			}
		}
		setMadhmounDraft(madhmounNumber);
		setBlnDraft(blnNumber);
		setReraDraft(reraNumber);
		setIsEditing(false);
	};

	const handleCopy = async (value: string, key: string) => {
		try {
			if (typeof navigator !== "undefined" && navigator.clipboard) {
				await navigator.clipboard.writeText(value);
				setCopyTarget(key);
				window.setTimeout(() => setCopyTarget(""), 1500);
			}
		} catch (_err) {
			//? Silent failure — clipboard is optional UX; user can still tap-and-hold to copy.
		}
	};

	const handleVerifyPermit = async () => {
		//? Copy the permit number first so the user can paste it into the ADREC verification form.
		if (madhmounNumber && typeof navigator !== "undefined" && navigator.clipboard) {
			try {
				await navigator.clipboard.writeText(madhmounNumber);
				setCopyTarget("madhmoun");
				window.setTimeout(() => setCopyTarget(""), 1500);
			} catch (_err) {
				//? Silent — clipboard write is best-effort; we still proceed to open the verifier.
			}
		}
		if (typeof window !== "undefined") {
			window.open("https://adrec.gov.ae/en/verify-document", "_blank", "noopener,noreferrer");
		}
	};

	const inputSx = {
		width: "100%",
		"& .MuiOutlinedInput-root": {
			height: "3rem",
			borderRadius: "0.75rem",
			fontFamily: "inherit",
			fontSize: "0.875rem",
		},
		"& .MuiOutlinedInput-input::placeholder": {
			color: "#7E7D7D",
			opacity: 1,
		},
	} as const;

	/* ---------- Mobile read view ---------- */

	const visibleCplCount = showAllCpl ? cplNumbers.length : Math.min(2, cplNumbers.length);
	const visibleCpls = cplNumbers.slice(0, visibleCplCount);
	const remainingCplCount = cplNumbers.length - visibleCpls.length;

	const readView = (
		<FieldCardGrid>
			{/* Permit (Madhmoun) */}
			<FieldCard>
				<PermitTopRow>
					<PermitLabelGroup>
						<LabelWrap>
							<FieldLabelMobile>{localLang.permit}</FieldLabelMobile>
							<ClickableInfoTooltip
								title={tooltipCopy.madhmoun}
								isOpen={openTip === "tip-mobile-madhmoun"}
								onToggle={() => handleTipToggle("tip-mobile-madhmoun")}
								mobile={isMobile}
							/>
						</LabelWrap>
						<FieldLabelSub>{localLang.madhmoun_sub}</FieldLabelSub>
					</PermitLabelGroup>
					{madhmounNumber !== "" ? (
						<ActionLink onClick={handleVerifyPermit}>{localLang.verify_permit}</ActionLink>
					) : null}
				</PermitTopRow>
				<SimpleFieldRow style={{ justifyContent: "flex-end" }}>
					{madhmounNumber !== "" ? (
						<PermitValueRow>
							<PermitScrollableValue>{madhmounNumber}</PermitScrollableValue>
							<Tooltip
								title={copyTarget === "madhmoun" ? localLang.copied : ""}
								open={copyTarget === "madhmoun"}
								placement="top"
								arrow
							>
								<CopyIconButton
									centerRipple
									onClick={() => handleCopy(madhmounNumber, "madhmoun")}
									aria-label="Copy Madhmoun permit number"
								>
									<ContentCopyRounded sx={{ fontSize: "1rem", flexShrink: 0 }} />
								</CopyIconButton>
							</Tooltip>
						</PermitValueRow>
					) : (
						<FieldValueMobile>—</FieldValueMobile>
					)}
				</SimpleFieldRow>
			</FieldCard>

			{/* BLN */}
			<FieldCard>
				<SimpleFieldRow>
					<LabelWrap>
						<FieldLabelMobile>{localLang.bln}</FieldLabelMobile>
						<ClickableInfoTooltip
							title={tooltipCopy.bln}
							isOpen={openTip === "tip-mobile-bln"}
							onToggle={() => handleTipToggle("tip-mobile-bln")}
							mobile={isMobile}
						/>
					</LabelWrap>
					<FieldValueMobile>{blnNumber !== "" ? blnNumber : "—"}</FieldValueMobile>
				</SimpleFieldRow>
			</FieldCard>

			{/* ADM */}
			<FieldCard>
				<SimpleFieldRow>
					<LabelWrap>
						<FieldLabelMobile>{localLang.adm}</FieldLabelMobile>
						<ClickableInfoTooltip
							title={tooltipCopy.adm}
							isOpen={openTip === "tip-mobile-adm"}
							onToggle={() => handleTipToggle("tip-mobile-adm")}
							mobile={isMobile}
						/>
					</LabelWrap>
					<FieldValueMobile>{admNumber !== "" ? admNumber : "—"}</FieldValueMobile>
				</SimpleFieldRow>
			</FieldCard>

			{/* CPL */}
			<FieldCard>
				<SimpleFieldRow style={{ justifyContent: "flex-start" }}>
					<LabelWrap>
						<FieldLabelMobile>{localLang.cpl}</FieldLabelMobile>
						<ClickableInfoTooltip
							title={tooltipCopy.cpl}
							isOpen={openTip === "tip-mobile-cpl"}
							onToggle={() => handleTipToggle("tip-mobile-cpl")}
							mobile={isMobile}
						/>
					</LabelWrap>
				</SimpleFieldRow>
				{cplNumbers.length > 0 ? (
					<ChipRow>
						{visibleCpls.map((value, idx) => (
							<CplChip key={`${value}-${idx}`}>
								<CheckCircleRounded sx={{ fontSize: "0.875rem", color: COLOR_LINK }} />
								<ChipText>{truncateMiddle(value, 11)}</ChipText>
							</CplChip>
						))}
						{remainingCplCount > 0 ? (
							<MoreChip
								type="button"
								onClick={() => setShowAllCpl(true)}
							>
								+{remainingCplCount} {localLang.more} →
							</MoreChip>
						) : null}
						{showAllCpl && cplNumbers.length > 2 ? (
							<MoreChip
								type="button"
								onClick={() => setShowAllCpl(false)}
							>
								—
							</MoreChip>
						) : null}
					</ChipRow>
				) : (
					<FieldValueMobile style={{ textAlign: "left" }}>—</FieldValueMobile>
				)}
			</FieldCard>
		</FieldCardGrid>
	);

	/* ---------- Edit view (used at all widths; layout swaps via CSS grid) ---------- */

	const editView = (
		<React.Fragment>
			<div
				style={{
					display: "grid",
					gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
					rowGap: "1.125rem",
					columnGap: "1.5rem",
					width: "100%",
				}}
			>
				<EditCell>
					<LabelWrap>
						<FieldLabelDesktop>{localLang.permit_madhmoun_inline} *</FieldLabelDesktop>
						<ClickableInfoTooltip
							title={tooltipCopy.madhmoun}
							isOpen={openTip === "tip-edit-madhmoun"}
							onToggle={() => handleTipToggle("tip-edit-madhmoun")}
						/>
					</LabelWrap>
					<TextField
						variant="outlined"
						placeholder={localLang.madhmoun_placeholder}
						value={madhmounDraft}
						disabled={isSaving}
						onChange={(e) => setMadhmounDraft(e.target.value)}
						sx={inputSx}
					/>
				</EditCell>

				<EditCell>
					<LabelWrap>
						<FieldLabelDesktop>{localLang.bln} *</FieldLabelDesktop>
						<ClickableInfoTooltip
							title={tooltipCopy.bln}
							isOpen={openTip === "tip-edit-bln"}
							onToggle={() => handleTipToggle("tip-edit-bln")}
						/>
					</LabelWrap>
					<TextField
						variant="outlined"
						placeholder={localLang.bln_placeholder}
						value={blnDraft}
						disabled={isSaving}
						onChange={(e) => setBlnDraft(e.target.value)}
						sx={inputSx}
					/>
				</EditCell>
			</div>

			<div
				style={{
					display: "grid",
					gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
					rowGap: "1.125rem",
					columnGap: "1.5rem",
					width: "100%",
					marginTop: "1.5rem",
				}}
			>
				<ReadCell>
					<LabelWrap>
						<FieldLabelDesktop>{localLang.adm}</FieldLabelDesktop>
						<ClickableInfoTooltip
							title={tooltipCopy.adm}
							isOpen={openTip === "tip-edit-adm"}
							onToggle={() => handleTipToggle("tip-edit-adm")}
						/>
					</LabelWrap>
					<FieldValueDesktop>{admNumber !== "" ? admNumber : "—"}</FieldValueDesktop>
				</ReadCell>

				<ReadCell>
					<LabelWrap>
						<FieldLabelDesktop>{localLang.cpl}</FieldLabelDesktop>
						<ClickableInfoTooltip
							title={tooltipCopy.cpl}
							isOpen={openTip === "tip-edit-cpl"}
							onToggle={() => handleTipToggle("tip-edit-cpl")}
						/>
					</LabelWrap>
					<FieldValueDesktop multi={cplNumbers.length > 1}>{cplDisplayDesktop}</FieldValueDesktop>
				</ReadCell>
			</div>

			<ButtonRow>
				{!isSaving ? (
					<BeegruButton
						flavor="primary"
						variant="contained"
						size="large"
						fullWidth
						onClick={handleSave}
						sx={{
							height: "3.125rem",
							borderRadius: "0.75rem",
							textTransform: "none",
							fontSize: "1rem",
							fontWeight: 400,
						}}
					>
						{localLang.update}
					</BeegruButton>
				) : (
					<CircularProgress sx={{ marginTop: "1rem" }} />
				)}
			</ButtonRow>
		</React.Fragment>
	);

	//? India (RERA-only) views — a single labelled field; UAE keeps the permit/broker UI above.
	const indiaReadView = (
		<FieldCardGrid>
			<FieldCard>
				<SimpleFieldRow>
					<FieldValueMobile>{reraNumber !== "" ? reraNumber : "—"}</FieldValueMobile>
				</SimpleFieldRow>
			</FieldCard>
		</FieldCardGrid>
	);

	const indiaEditView = (
		<React.Fragment>
			<div style={{ display: "grid", gridTemplateColumns: "1fr", rowGap: "1.125rem", width: "100%" }}>
				<EditCell>
					<TextField
						variant="outlined"
						placeholder={localLang.rera_placeholder}
						value={reraDraft}
						disabled={isSaving}
						onChange={(e) => setReraDraft(e.target.value)}
						sx={inputSx}
					/>
				</EditCell>
			</div>
			<ButtonRow>
				{!isSaving ? (
					<BeegruButton
						flavor="primary"
						variant="contained"
						size="large"
						fullWidth
						onClick={handleSave}
						sx={{
							height: "3.125rem",
							borderRadius: "0.75rem",
							textTransform: "none",
							fontSize: "1rem",
							fontWeight: 400,
						}}
					>
						{localLang.update}
					</BeegruButton>
				) : (
					<CircularProgress sx={{ marginTop: "1rem" }} />
				)}
			</ButtonRow>
		</React.Fragment>
	);

	return (
		<Container
			id="regulatory-info"
			elevation={0}
		>
			<Snackbar
				open={regulatory_info_snackbar}
				autoHideDuration={3000}
				onClose={() => dispatch(setPropertySnackbarThunk(false))}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<Alert
					onClose={() => dispatch(setPropertySnackbarThunk(false))}
					severity="success"
					variant="filled"
					sx={{ width: "100%", borderRadius: "0.75rem" }}
				>
					{localLang.snackValue}
				</Alert>
			</Snackbar>

			<HeaderWrap compact={isEditing}>
				<HeaderRow>
					<HeadingGroup>
						<Heading>{isIndia ? localLang.rera : localLang.title}</Heading>
						<VerifiedRounded sx={{ fontSize: "1.25rem", color: COLOR_LINK, flexShrink: 0 }} />
					</HeadingGroup>

					{canEdit ? (
						!isEditing ? (
							<IconButton
								color="primary"
								sx={{ padding: "0.25rem" }}
								onClick={() => setIsEditing(true)}
							>
								<EditOutlined
									sx={{
										fontSize: "1.25rem",
										[theme.breakpoints.up("md1190")]: { fontSize: "1.5rem" },
									}}
								/>
							</IconButton>
						) : (
							<IconButton
								color="primary"
								sx={{ padding: "0rem" }}
								onClick={handleCancel}
								disabled={isSaving}
							>
								<Close
									sx={{
										fontSize: "1.25rem",
										[theme.breakpoints.up("md1190")]: { fontSize: "1.75rem" },
									}}
								/>
							</IconButton>
						)
					) : null}
				</HeaderRow>
			</HeaderWrap>

			{!isEditing ? (isIndia ? indiaReadView : readView) : (isIndia ? indiaEditView : editView)}
		</Container>
	);
};

export default RegulatoryInfoCard;
